import type {
  RecordLinkItem,
  RecordLinkManager,
  RecordLinkInfo,
  RecordLinkOption,
} from '../types';
import { ApplicationController, raw } from '@aha-app/mvc';
import type WhiteboardController from './WhiteboardController';
import {
  isWhiteboardRecordTypeName,
  normalizeRecordType,
} from '../utils/record';
import tools from '../tools';
import type Connector from '../objects/Connector';
import { hasConnectorAttachmentBehaviours } from '../objects/guards';
import type Canvas from '../objects/Canvas';
import type DependencyLineManager from '../utils/DependencyLineManager';
import trackEvent from '../utils/analytics';

interface State {
  recordLinkItems: RecordLinkItem[];
  loading: boolean;
  saving: boolean;
}

type TLinkPairs = [RecordLinkItem, Aha.RecordLink][];

export default class RecordLinkController extends ApplicationController<State> {
  static initialState = {
    recordLinkItems: [],
    loading: true,
    saving: false,
  };

  whiteboardController: WhiteboardController;
  canvas: Canvas;
  recordLinkManager?: RecordLinkManager;
  dependencyLineManager: DependencyLineManager;

  startController(canvas: Canvas, whiteboardController: WhiteboardController) {
    this.whiteboardController = whiteboardController;
    this.canvas = canvas;
    this.recordLinkManager = this.whiteboardController.recordLinkManager;
    this.dependencyLineManager =
      this.whiteboardController.dependencyLineManager;
    this.whiteboardController.setOpenRecordLinkModalFunc(
      this.actionSetRecordLinkItems
    );
  }

  get showRecordLinkModal() {
    return this.state.recordLinkItems.length > 0;
  }

  supportedRecordLinkOptions() {
    return this.recordLinkManager?.supportedRecordLinkOptions();
  }

  normalizeRecords(
    recordLinkItem: RecordLinkItem,
    linkTypeOption: RecordLinkOption
  ): RecordLinkInfo | null {
    const { parentRecord, childRecord } = recordLinkItem;
    const parentTypename = linkTypeOption.inverse
      ? childRecord!.typename
      : parentRecord!.typename;
    const childTypename = linkTypeOption.inverse
      ? parentRecord!.typename
      : childRecord!.typename;

    // Ensure this is a valid record type (i.e. not Undefined or NotFound)
    if (
      !isWhiteboardRecordTypeName(parentTypename) ||
      !isWhiteboardRecordTypeName(childTypename)
    ) {
      return null;
    }

    const parentId = linkTypeOption.inverse
      ? childRecord!.id
      : parentRecord!.id;
    const childId = linkTypeOption.inverse ? parentRecord!.id : childRecord!.id;
    const parentType = normalizeRecordType(parentTypename);
    const childType = normalizeRecordType(childTypename);

    return {
      parentRecord: {
        id: parentId,
        type: parentType,
      },
      childRecord: {
        id: childId,
        type: childType,
      },
    };
  }

  async fetchRecordInfoForLink(item: RecordLinkItem) {
    const { parentType, parentId, childType, childId } = item;
    return await Promise.all([
      this.whiteboardController.fetchRecordInfo(parentType, parentId),
      this.whiteboardController.fetchRecordInfo(childType, childId),
    ]);
  }

  async createRecordLink(
    recordLinkItem: RecordLinkItem,
    linkTypeOption: RecordLinkOption
  ): Promise<
    | { result: false; recordLink: null }
    | { result: boolean; recordLink: Aha.RecordLink }
  > {
    if (
      this.recordLinkManager === undefined ||
      !recordLinkItem.parentRecord ||
      !recordLinkItem.childRecord
    ) {
      return { result: false, recordLink: null };
    }
    const { parentRecord, childRecord } = recordLinkItem;
    const parentTypename = linkTypeOption.inverse
      ? childRecord.typename
      : parentRecord.typename;
    const childTypename = linkTypeOption.inverse
      ? parentRecord.typename
      : childRecord.typename;

    // Ensure this is a valid record type (i.e. not Undefined or NotFound)
    if (
      !isWhiteboardRecordTypeName(parentTypename) ||
      !isWhiteboardRecordTypeName(childTypename)
    ) {
      return { result: false, recordLink: null };
    }

    const parentId = linkTypeOption.inverse ? childRecord.id : parentRecord.id;
    const childId = linkTypeOption.inverse ? parentRecord.id : childRecord.id;
    const parentType = normalizeRecordType(parentTypename);
    const childType = normalizeRecordType(childTypename);

    return await this.recordLinkManager.createRecordLink({
      parentRecord: {
        id: parentId,
        type: parentType,
      },
      childRecord: {
        id: childId,
        type: childType,
      },
      linkType: linkTypeOption.value,
    });
  }

  createRecordLinkLine(
    recordLinkItem: RecordLinkItem,
    recordLink: Aha.RecordLink,
    inverse: boolean
  ) {
    const parentRecord = recordLinkItem.parentRecord!;
    const childRecord = recordLinkItem.childRecord!;
    const parentId = inverse ? childRecord.id : parentRecord.id;
    const childId = inverse ? parentRecord.id : childRecord.id;
    const objects = this.canvas.getObjects();
    const parentObj = objects.find(obj => obj.recordId === parentId);
    const childObj = objects.find(obj => obj.recordId === childId);
    // Create dependency line
    if (
      hasConnectorAttachmentBehaviours(parentObj) &&
      hasConnectorAttachmentBehaviours(childObj)
    ) {
      const lineRouting = recordLinkItem.connector?.lineRouting;
      const [a1, a2] = recordLinkItem.connector?.attachments;
      this.dependencyLineManager.createConnector({
        parentObj,
        childObj,
        recordLink,
        lineRouting: lineRouting ?? undefined,
        attachments: a1 && a2 ? [a1, a2] : undefined,
      });
    }
    this.canvas.requestRenderAll();
  }

  createRecordLinkLines(pairs: TLinkPairs, inverse: boolean) {
    pairs.forEach(([recordLinkItem, recordLink]) => {
      this.createRecordLinkLine(recordLinkItem, recordLink, inverse);
    });
  }

  findExistingConnectors(
    recordLinkItem: RecordLinkItem,
    recordLink: Aha.RecordLink,
    inverse: boolean
  ) {
    const parentRecord = recordLinkItem.parentRecord!;
    const childRecord = recordLinkItem.childRecord!;
    const parentId = inverse ? childRecord.id : parentRecord.id;
    const childId = inverse ? parentRecord.id : childRecord.id;
    const toDelete: fabric.Object[] = [];
    // Delete any existing record link lines, if they exist. This may delete a
    // dependency connector for a different pair of record instances with the same
    // record link.
    const dependencyLineObjects =
      this.dependencyLineManager.getDependencyLineObjects();
    const existingDependecy = dependencyLineObjects.find(
      ({ dependencyLineInfo }) => {
        return (
          dependencyLineInfo?.parentRecordId === parentId &&
          dependencyLineInfo?.childRecordId === childId &&
          dependencyLineInfo?.linkType === recordLink.linkType
        );
      }
    );
    if (existingDependecy) {
      toDelete.push(existingDependecy);
    }
    // Remove connector used to create this record link
    if (
      recordLinkItem.connector &&
      this.canvas.findFirstObject(
        (object): object is Connector =>
          object.id === recordLinkItem.connector.id
      )
    ) {
      toDelete.push(raw(recordLinkItem.connector));
    }
    return toDelete;
  }

  deleteExistingConnectors(pairs: TLinkPairs, inverse: boolean) {
    const toDelete: fabric.Object[] = [];
    pairs.forEach(([recordLinkItem, recordLink]) => {
      toDelete.push(
        ...this.findExistingConnectors(recordLinkItem, recordLink, inverse)
      );
    });
    this.canvas.discardActiveObject();
    tools.ToolDelete.deleteObjects(toDelete, this.whiteboardController);
    this.canvas.remove(...toDelete);
  }

  handleRecordLinkError(result: {
    result: boolean;
    recordLinks: Aha.RecordLink[];
  }) {
    let error = 'Failed to create record link';

    // Display a more informative message if there is just one record link
    if (result.recordLinks.length === 1) {
      // @ts-expect-error aha.d.ts does not have the errors field that does exist here
      error = result.recordLinks[0].errors?.errors.value.fullMessages.join(',');
    } else {
      const resultsWithErrors = result.recordLinks.filter(
        // @ts-expect-error aha.d.ts does not have the errors field that does exist here
        recordLink => recordLink.errors?.errors.value?.fullMessages
      );

      // If there are multiple record links, and not all of them failed, show
      // a generic message
      if (resultsWithErrors.length !== this.state.recordLinkItems.length) {
        error = 'Some record links were not created';
      }
    }
    this.whiteboardController.actionShowMessage({
      message: error,
      type: 'error',
    });
  }

  // Really, this will set recordLinkItems, but we are tracking the items being
  // passed, hence the name.
  async actionSetRecordLinkItems(items: RecordLinkItem[]) {
    if (items && items.length > 0) {
      this.state.loading = true;
      this.state.recordLinkItems = items;

      // Track analytics for modal opening
      trackEvent(
        'Feature-7405380385692930693',
        'User opens Create dependency modal in whiteboards'
      );

      // Load the data for the records being linked
      const fetchedItems = await Promise.all(
        items.map(async item => await this.fetchRecordInfoForLink(item))
      );
      if (this.state.recordLinkItems !== null) {
        fetchedItems.forEach(([parentRecord, childRecord], index) => {
          if (this.state.recordLinkItems !== null) {
            if (parentRecord && childRecord) {
              this.state.recordLinkItems[index].parentRecord = parentRecord;
              this.state.recordLinkItems[index].childRecord = childRecord;
            }
          }
        });
        this.state.loading = false;
      }
    } else {
      this.actionResetRecordLinkState();
    }
  }

  async actionCreateRecordLinks(linkTypeOption: RecordLinkOption) {
    if (
      !this.recordLinkManager
        ?.supportedRecordLinkTypes()
        .some(type => type === linkTypeOption.value)
    ) {
      console.error('Invalid record link type');
      return;
    }

    this.state.saving = true;

    // Normalize types and change parent/child positions if linkTypeOption is inverse
    const normalizedItems = this.state.recordLinkItems
      .map(item => this.normalizeRecords(item, linkTypeOption))
      .filter(Boolean);

    trackEvent(
      'Feature-7405380385692930693',
      'User creates dependency from whiteboard',
      {
        'Links created': normalizedItems.length,
      }
    );

    const result = await this.recordLinkManager.createRecordLinks({
      items: normalizedItems,
      linkType: linkTypeOption.value,
    });

    if (result?.result) {
      // Find corresponding pairs of recordLinkItem and reocrdLink
      const pairs: TLinkPairs = [];
      this.state.recordLinkItems.forEach(item => {
        const recordLink = result.recordLinks.find(
          link =>
            link.parentRecordId === item.parentId &&
            link.childRecordId === item.childId
        );
        if (recordLink) {
          pairs.push([item, recordLink]);
        }
      });

      // Delete the old connectors and add new dependency lines
      this.deleteExistingConnectors(pairs, linkTypeOption.inverse);
      this.createRecordLinkLines(pairs, linkTypeOption.inverse);
    } else {
      this.handleRecordLinkError(result);
    }

    this.actionResetRecordLinkState();
  }

  actionResetRecordLinkState() {
    this.state.recordLinkItems = [];
    this.state.loading = true;
    this.state.saving = false;
  }
}
